import React, { useEffect, useState, useRef } from 'react';
import { Star, GitFork, ExternalLink, RefreshCw, Bookmark, BookmarkCheck, Share2, Check } from 'lucide-react';
import { Language } from '../utils/i18n';
import { isFavorite, addFavorite, removeFavorite } from '../utils/storage';

interface TrendingRepo {
  fullName: string;
  owner: string;
  name: string;
  description: string;
  language: string;
  stars: string;
  forks: string;
  todayStars: string;
  url: string;
}

interface TrendingCardsProps {
  onRepoClick: (repoName: string) => void;
  lang: Language;
  onProjectNamesFetched?: (names: string[]) => void; // 新增：当获取到项目名称时回调
}

// 编程语言颜色映射
const LANGUAGE_COLORS: Record<string, string> = {
  'JavaScript': '#f1e05a',
  'TypeScript': '#3178c6',
  'Python': '#3572A5',
  'Go': '#00ADD8',
  'Rust': '#dea584',
  'C++': '#f34b7d',
  'Java': '#b07219',
  'Ruby': '#701516',
  'PHP': '#4F5D95',
  'Swift': '#F05138',
  'Kotlin': '#A97BFF',
  'Dart': '#00B4AB',
};

export default function TrendingCards({ onRepoClick, lang, onProjectNamesFetched }: TrendingCardsProps) {
  // 初始化时就设置默认数据，确保卡片始终显示
  const defaultRepos: TrendingRepo[] = [
    { fullName: "facebook/react", owner: "facebook", name: "react", description: "The library for web and native user interfaces", language: "JavaScript", stars: "227k", forks: "46k", todayStars: "", url: "https://github.com/facebook/react" },
    { fullName: "microsoft/vscode", owner: "microsoft", name: "vscode", description: "Visual Studio Code", language: "TypeScript", stars: "162k", forks: "28k", todayStars: "", url: "https://github.com/microsoft/vscode" },
    { fullName: "tensorflow/tensorflow", owner: "tensorflow", name: "tensorflow", description: "An Open Source Machine Learning Framework", language: "C++", stars: "185k", forks: "95k", todayStars: "", url: "https://github.com/tensorflow/tensorflow" },
    { fullName: "vuejs/core", owner: "vuejs", name: "core", description: "Vue.js progressive framework", language: "TypeScript", stars: "47k", forks: "8k", todayStars: "", url: "https://github.com/vuejs/core" },
    { fullName: "golang/go", owner: "golang", name: "go", description: "The Go programming language", language: "Go", stars: "123k", forks: "17k", todayStars: "", url: "https://github.com/golang/go" },
    { fullName: "rust-lang/rust", owner: "rust-lang", name: "rust", description: "Empowering everyone to build reliable software", language: "Rust", stars: "97k", forks: "12k", todayStars: "", url: "https://github.com/rust-lang/rust" },
    { fullName: "vercel/next.js", owner: "vercel", name: "next.js", description: "The React Framework", language: "JavaScript", stars: "125k", forks: "26k", todayStars: "", url: "https://github.com/vercel/next.js" },
    { fullName: "microsoft/typescript", owner: "microsoft", name: "typescript", description: "TypeScript superset of JavaScript", language: "TypeScript", stars: "102k", forks: "13k", todayStars: "", url: "https://github.com/microsoft/typescript" }
  ];

  const [repos, setRepos] = useState<TrendingRepo[]>(defaultRepos);
  const [loading, setLoading] = useState(false); // 改为 false，立即显示默认数据
  const [isUsingDefault, setIsUsingDefault] = useState(true); // 默认就是使用默认数据
  const [isRetrying, setIsRetrying] = useState(false);
  const [copiedShare, setCopiedShare] = useState<string | null>(null);
  const [favoriteStatus, setFavoriteStatus] = useState<Record<string, boolean>>({});
  const retryIntervalRef = useRef<NodeJS.Timeout | null>(null);
  const retryCountRef = useRef(0);
  const hasNotifiedNamesRef = useRef(false);
  const hasWarmedRef = useRef(false);
  const MAX_RETRIES = 10;
  const RETRY_INTERVAL = 30000;

  // 初始化收藏状态
  useEffect(() => {
    const status: Record<string, boolean> = {};
    repos.forEach(repo => {
      status[repo.name] = isFavorite(repo.name);
    });
    setFavoriteStatus(status);
  }, [repos]);

  // 处理收藏切换
  const handleFavoriteToggle = (e: React.MouseEvent, repoName: string) => {
    e.preventDefault();
    e.stopPropagation();

    const isFav = isFavorite(repoName);
    if (isFav) {
      removeFavorite(repoName);
    } else {
      addFavorite(repoName);
    }

    setFavoriteStatus(prev => ({
      ...prev,
      [repoName]: !isFav
    }));
  };

  // 处理分享
  const handleShare = (e: React.MouseEvent, repoName: string) => {
    e.preventDefault();
    e.stopPropagation();

    const shareUrl = `${window.location.origin}?q=${encodeURIComponent(repoName)}`;

    if (navigator.share) {
      navigator.share({
        title: repoName,
        url: shareUrl
      }).catch(() => {
        // 降级到复制链接
        fallbackShare(repoName);
      });
    } else {
      fallbackShare(repoName);
    }
  };

  const fallbackShare = (repoName: string) => {
    const shareUrl = `${window.location.origin}?q=${encodeURIComponent(repoName)}`;
    navigator.clipboard.writeText(shareUrl);
    setCopiedShare(repoName);
    setTimeout(() => setCopiedShare(null), 2000);
  };

  /**
   * 触发预热缓存
   */
  const triggerWarmup = async (queries: string[]) => {
    if (hasWarmedRef.current || queries.length === 0) return;

    try {
      console.log('🔥 触发预热缓存，搜索词数量:', queries.length);
      const response = await fetch('/api/warmup', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ queries })
      });

      if (response.ok) {
        const data = await response.json();
        console.log('✅ 预热任务已启动:', data);
        hasWarmedRef.current = true;
      }
    } catch (error) {
      console.log('⚠️ 预热请求失败:', error);
    }
  };

  // 清理定时器
  useEffect(() => {
    return () => {
      if (retryIntervalRef.current) {
        clearInterval(retryIntervalRef.current);
      }
    };
  }, []);

  // 获取 Trending 数据
  const fetchTrending = async (isRetry = false): Promise<boolean> => {
    try {
      if (isRetry) {
        setIsRetrying(true);
        console.log('🔄 后台重试获取 Trending...');
      }

      const response = await fetch('/api/trending');
      const data = await response.json();

      // 检查是否是真实数据（不是默认列表）
      // 真实数据通常有今日星标增长
      const hasRealData = data.repos && data.repos.some((repo: TrendingRepo) => repo.todayStars);
      const hasError = !!data.error;

      if (data.repos && data.repos.length > 0) {
        const newRepos = data.repos.slice(0, 8);
        setRepos(newRepos);
        setLoading(false);

        // 触发预热缓存（异步执行，不阻塞）
        const projectNames = newRepos.map(repo => repo.name);
        if (!isRetry && !hasWarmedRef.current) {
          // 延迟1秒触发预热，避免阻塞页面渲染
          setTimeout(() => triggerWarmup(projectNames), 1000);
        }

        // 通知父组件项目名称（仅通知一次）
        if (!hasNotifiedNamesRef.current && onProjectNamesFetched) {
          onProjectNamesFetched(projectNames);
          hasNotifiedNamesRef.current = true;
        }

        if (hasRealData) {
          // 有今日星标数据，说明是真实的 Trending
          setIsUsingDefault(false);
          console.log('🔥 GitHub Trending 真实数据:', data.repos.length, 'repos');
          // 成功获取真实数据，停止重试
          if (retryIntervalRef.current) {
            clearInterval(retryIntervalRef.current);
            retryIntervalRef.current = null;
          }
          setIsRetrying(false);
          return true;
        } else {
          // 使用默认列表
          setIsUsingDefault(true);
          console.log('📋 GitHub Trending 使用默认列表');
          // 默认列表也要启动后台重试
          if (!isRetry && !retryIntervalRef.current) {
            startRetryMechanism();
          }
          return false;
        }
      }

      return false;
    } catch (error) {
      console.log('⚠️ 获取 Trending 失败:', error);
      if (isRetry) {
        setIsRetrying(false);
      }
      return false;
    }
  };

  // 启动后台重试机制
  const startRetryMechanism = () => {
    if (retryIntervalRef.current) return; // 已经在重试中

    console.log('⏰ 启动后台重试机制（每30秒）');
    retryCountRef.current = 0;

    retryIntervalRef.current = setInterval(async () => {
      retryCountRef.current++;

      if (retryCountRef.current > MAX_RETRIES) {
        console.log('🛑 达到最大重试次数，停止重试');
        if (retryIntervalRef.current) {
          clearInterval(retryIntervalRef.current);
          retryIntervalRef.current = null;
        }
        setIsRetrying(false);
        return;
      }

      const success = await fetchTrending(true);
      if (success) {
        console.log('✅ 重试成功，获取到真实数据！');
      } else {
        console.log(`⏳ 重试 ${retryCountRef.current}/${MAX_RETRIES} 次失败，继续等待...`);
      }
    }, RETRY_INTERVAL);
  };

  useEffect(() => {
    const initTrending = async () => {
      console.log('🔄 初始化 Trending 卡片...');
      const success = await fetchTrending();

      if (!success) {
        // 使用默认列表，启动后台重试
        console.log('📋 使用默认列表，启动后台重试');
        setIsUsingDefault(true);
        startRetryMechanism();
      }

      // 无论成功失败，都触发预热（使用当前的 repos）
      if (!hasWarmedRef.current && repos.length > 0) {
        const projectNames = repos.map(r => r.name);
        setTimeout(() => triggerWarmup(projectNames), 500);
      }
    };

    initTrending();
  }, []);

  if (loading) {
    return (
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 max-w-7xl mx-auto">
        {[...Array(8)].map((_, i) => (
          <div key={i} className="animate-pulse bg-white dark:bg-gray-800 rounded-xl p-4 border border-gray-200 dark:border-gray-700">
            <div className="h-4 bg-gray-200 dark:bg-gray-700 rounded mb-2 w-3/4"></div>
            <div className="h-3 bg-gray-200 dark:bg-gray-700 rounded mb-4 w-1/2"></div>
            <div className="h-3 bg-gray-200 dark:bg-gray-700 rounded mb-2"></div>
            <div className="h-3 bg-gray-200 dark:bg-gray-700 rounded w-2/3"></div>
          </div>
        ))}
      </div>
    );
  }

  if (repos.length === 0) {
    return null;
  }

  return (
    <div className="max-w-7xl mx-auto">
      {/* 标题 */}
      <div className="flex items-center justify-between mb-4">
        <h2 className="text-lg font-semibold text-gray-800 dark:text-gray-200">
          🔥 {lang === 'zh' ? 'GitHub 热门项目' : 'GitHub Trending'}
        </h2>

        {/* 后台重试提示 */}
        {isUsingDefault && (
          <div className="flex items-center gap-2 text-xs text-gray-500 dark:text-gray-400">
            {isRetrying ? (
              <>
                <RefreshCw className="w-3.5 h-3.5 animate-spin" />
                <span>{lang === 'zh' ? '后台尝试获取真实数据...' : 'Fetching real data...'}</span>
              </>
            ) : (
              <span className="text-yellow-600 dark:text-yellow-400">
                ⚠️ {lang === 'zh' ? '显示默认数据' : 'Showing default data'}
              </span>
            )}
          </div>
        )}
      </div>

      {/* 卡片网格 */}
      <div className="grid grid-cols-2 gap-6 max-w-5xl mx-auto">
        {repos.map((repo) => (
          <div
            key={repo.fullName}
            className="group relative bg-white dark:bg-gray-800 rounded-xl p-5 border border-gray-200 dark:border-gray-700 shadow-sm hover:shadow-xl hover:border-[#165DFF] dark:hover:border-[#165DFF] hover:-translate-y-1 transition-all duration-300"
          >
            <a
              href={repo.url}
              target="_blank"
              rel="noopener noreferrer"
              className="block"
              onClick={(e) => {
                e.preventDefault();
                onRepoClick(repo.name);
              }}
            >
              {/* 项目名称 */}
              <div className="flex items-start justify-between mb-3">
                <h3 className="font-semibold text-base text-gray-800 dark:text-gray-200 group-hover:text-[#165DFF] transition-colors truncate flex-1">
                  {repo.owner}/<span className="font-bold">{repo.name}</span>
                </h3>
                <div className="flex items-center gap-2 flex-shrink-0 ml-2">
                  {/* 收藏按钮 */}
                  <button
                    onClick={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                      handleFavoriteToggle(e, repo.name);
                    }}
                    className="p-1.5 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors"
                    title={favoriteStatus[repo.name] ? '取消收藏' : '收藏'}
                  >
                    {favoriteStatus[repo.name] ? (
                      <BookmarkCheck className="w-4 h-4 text-[#165DFF]" />
                    ) : (
                      <Bookmark className="w-4 h-4 text-gray-400" />
                    )}
                  </button>
                  {/* 分享按钮 */}
                  <button
                    onClick={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                      handleShare(e, repo.name);
                    }}
                    className="p-1.5 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors"
                    title="分享"
                  >
                    {copiedShare === repo.name ? (
                      <Check className="w-4 h-4 text-green-500" />
                    ) : (
                      <Share2 className="w-4 h-4 text-gray-400" />
                    )}
                  </button>
                  {/* 箭头图标 */}
                  <ExternalLink className="w-4 h-4 text-gray-400 group-hover:text-[#165DFF] transition-colors" />
                </div>
              </div>

            {/* 描述 */}
            <p className="text-sm text-gray-600 dark:text-gray-400 line-clamp-3 mb-4 min-h-[60px] leading-relaxed">
              {repo.description}
            </p>

            {/* 元信息 - 两列布局 */}
            <div className="grid grid-cols-2 gap-3 mb-3">
              {/* 左列 */}
              <div className="space-y-2">
                {/* 编程语言 */}
                {repo.language && (
                  <div className="flex items-center gap-2 text-xs text-gray-600 dark:text-gray-400">
                    <span
                      className="w-2.5 h-2.5 rounded-full flex-shrink-0"
                      style={{ backgroundColor: LANGUAGE_COLORS[repo.language] || '#8b949e' }}
                    />
                    <span className="truncate">{repo.language}</span>
                  </div>
                )}

                {/* 星标 */}
                <div className="flex items-center gap-2 text-xs text-gray-600 dark:text-gray-400">
                  <Star className="w-3.5 h-3.5 text-yellow-500 flex-shrink-0" />
                  <span>{repo.stars} stars</span>
                </div>
              </div>

              {/* 右列 */}
              <div className="space-y-2">
                {/* Fork */}
                <div className="flex items-center gap-2 text-xs text-gray-600 dark:text-gray-400">
                  <GitFork className="w-3.5 h-3.5 text-blue-500 flex-shrink-0" />
                  <span>{repo.forks} forks</span>
                </div>

                {/* 今日增长 */}
                {repo.todayStars && (
                  <div className="flex items-center gap-2 text-xs text-green-600 dark:text-green-400 font-medium">
                    <span className="text-lg leading-none">↑</span>
                    <span>+{repo.todayStars} {lang === 'zh' ? '今日' : 'today'}</span>
                  </div>
                )}
              </div>
            </div>

            {/* 底部标签 */}
            <div className="flex items-center justify-between pt-3 border-t border-gray-100 dark:border-gray-700">
              <div className="flex items-center gap-2 text-xs text-gray-400 dark:text-gray-500">
                <span className="px-2 py-1 bg-gray-100 dark:bg-gray-700 rounded-full">
                  {repo.name}
                </span>
              </div>
              <div className="text-xs text-gray-400 dark:text-gray-500">
                {lang === 'zh' ? '热门项目' : 'Trending'}
              </div>
            </div>
            </a>
          </div>
        ))}
      </div>
    </div>
  );
}

// 强制Vite包含此组件（防止tree-shaking）
export const TRENDING_COMPONENT_LOADED = true;
